import React, { useEffect, useMemo, useState } from "react";
import AppShell from "../components/AppShell";
import PageHero from "../components/PageHero";
import { api, formatError } from "../lib/api";
import { toast } from "sonner";
import { Save, RotateCcw, Info, CalendarClock } from "lucide-react";

const fmt = (n) => new Intl.NumberFormat("en-US").format(Math.round(n || 0));

export default function Planner() {
  const [templates, setTemplates] = useState(null);
  const [plan, setPlan] = useState(null);
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [activeCat, setActiveCat] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/planner/templates").then((r) => setTemplates(r.data)).catch(() => {});
    api.get("/planner/marketing").then((r) => {
      setPlan(r.data.plan);
      setActiveTemplate(r.data.template);
    }).catch(() => {});
  }, []);

  const load = (key) => {
    if (!templates) return;
    const t = templates.templates.find((x) => x.key === key);
    if (!t) return;
    setPlan(JSON.parse(JSON.stringify(t.plan)));
    setActiveTemplate(key);
    setActiveCat(0);
    toast.success(`Loaded ${t.name}`);
  };

  const setCell = (catIdx, itemIdx, monthIdx, val) => {
    const p = { ...plan, categories: plan.categories.map((c) => ({ ...c, items: c.items.map((it) => ({ ...it, months: [...it.months] })) })) };
    p.categories[catIdx].items[itemIdx].months[monthIdx] = Number(val) || 0;
    setPlan(p);
  };

  const setItemName = (catIdx, itemIdx, name) => {
    const p = { ...plan, categories: plan.categories.map((c) => ({ ...c, items: c.items.map((it) => ({ ...it })) })) };
    p.categories[catIdx].items[itemIdx].name = name;
    setPlan(p);
  };

  const save = async () => {
    setBusy(true);
    try { await api.post("/planner/marketing", { plan, template: activeTemplate }); toast.success("Plan saved"); }
    catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };

  const totals = useMemo(() => {
    if (!plan) return { grand: 0, byCat: [], byMonth: Array(12).fill(0) };
    const byCat = plan.categories.map((c) => ({
      key: c.key, name: c.name,
      total: c.items.reduce((s, it) => s + it.months.reduce((a, b) => a + (b || 0), 0), 0),
      byMonth: Array.from({ length: 12 }, (_, m) => c.items.reduce((s, it) => s + (it.months[m] || 0), 0)),
    }));
    const byMonth = Array.from({ length: 12 }, (_, m) => byCat.reduce((s, c) => s + c.byMonth[m], 0));
    return { grand: byCat.reduce((a, c) => a + c.total, 0), byCat, byMonth };
  }, [plan]);

  if (!plan || !templates) {
    return <AppShell title="Marketing Planner"><div className="text-muted-foreground">Loading planner…</div></AppShell>;
  }

  const months = templates.months;
  const currentCat = plan.categories[activeCat];

  return (
    <AppShell title="Marketing Planner">
      <div className="w-full">
        <PageHero
          eyebrow="Marketing Planner"
          icon={CalendarClock}
          title={<>Plan your annual marketing <span className="text-grad">budget</span></>}
          subtitle="Category-based budget with 12-month distribution. Start blank or load the Lithios example to see how a real CPG plan looks."
          right={
            <div className="flex flex-wrap gap-2">
              {templates.templates.map((t) => (
                <button key={t.key} onClick={() => load(t.key)} data-testid={`planner-template-${t.key}`}
                  className={["rounded-full px-4 py-2 text-sm font-semibold transition",
                    activeTemplate === t.key ? "grad-band text-white" : "border border-shell-border bg-white text-foreground hover:bg-muted"].join(" ")}>
                  {activeTemplate === t.key ? "✓ " : ""}{t.name}
                </button>
              ))}
              <button onClick={save} disabled={busy} className="btn-primary" data-testid="planner-save">
                <Save className="w-4 h-4" /> Save
              </button>
            </div>
          }
        />

        <div className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
          <Info className="w-3.5 h-3.5" />
          <p>Any changes are only persisted for your account. Switching template overwrites your current view.</p>
        </div>

        {/* Totals band */}
        <div className="mt-6 rounded-2xl p-5 sm:p-6 bg-shell-side text-white">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-baseline lg:justify-between lg:gap-6">
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-widest text-shell-accent font-semibold">Total annual budget</p>
              <p className="font-display text-3xl sm:text-4xl lg:text-5xl mt-1 break-words">${fmt(totals.grand)}</p>
            </div>
            <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-12 gap-x-2 gap-y-3 lg:flex-1 lg:min-w-0">
              {months.map((m, i) => (
                <div key={m} className="text-center min-w-0">
                  <p className="text-[10px] uppercase tracking-widest text-white/60">{m}</p>
                  <p className="text-xs font-semibold truncate">${fmt(totals.byMonth[i])}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-8 grid md:grid-cols-[260px_1fr] gap-6">
          {/* Category rail */}
          <aside className="surface p-3 h-fit md:sticky md:top-24" data-testid="planner-category-rail">
            <p className="px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Categories</p>
            <div className="flex flex-col">
              {plan.categories.map((c, i) => {
                const total = totals.byCat[i].total;
                return (
                  <button key={c.key} onClick={() => setActiveCat(i)} data-testid={`planner-cat-${c.key}`}
                    className={["flex items-center justify-between px-3 py-2 rounded-md text-sm transition",
                      activeCat === i ? "bg-shell-accent/20 text-shell-side font-semibold" : "text-foreground/80 hover:bg-muted"].join(" ")}>
                    <span className="truncate">{c.name}</span>
                    <span className="text-xs text-muted-foreground">${fmt(total)}</span>
                  </button>
                );
              })}
            </div>
          </aside>

          {/* Line items grid */}
          <div className="min-w-0">
            <div className="surface overflow-x-auto">
              <table className="w-full text-sm min-w-[900px]">
                <thead className="bg-muted text-left">
                  <tr className="[&>th]:px-3 [&>th]:py-3 [&>th]:font-semibold [&>th]:uppercase [&>th]:text-[10px] [&>th]:tracking-widest">
                    <th className="min-w-[220px]">{currentCat.name} — line item</th>
                    {months.map((m) => <th key={m} className="text-right">{m}</th>)}
                    <th className="text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {currentCat.items.map((it, i) => {
                    const rowTotal = it.months.reduce((a, b) => a + (b || 0), 0);
                    return (
                      <tr key={i} data-testid={`planner-row-${currentCat.key}-${i}`} className="border-t border-shell-border">
                        <td className="px-3 py-2">
                          <input value={it.name} onChange={(e) => setItemName(activeCat, i, e.target.value)}
                            data-testid={`planner-name-${currentCat.key}-${i}`}
                            className="w-full bg-transparent focus:bg-white border border-transparent focus:border-shell-border rounded px-2 py-1 text-sm" />
                        </td>
                        {it.months.map((v, m) => (
                          <td key={m} className="px-1 py-1">
                            <input type="number" value={v || ""} onChange={(e) => setCell(activeCat, i, m, e.target.value)}
                              data-testid={`planner-cell-${currentCat.key}-${i}-${m}`}
                              placeholder="0"
                              className="w-20 text-right rounded border border-transparent hover:border-shell-border focus:border-shell-accent bg-transparent focus:bg-white px-2 py-1 text-sm" />
                          </td>
                        ))}
                        <td className="px-3 py-2 text-right font-semibold whitespace-nowrap">${fmt(rowTotal)}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-shell-border bg-muted/60">
                    <td className="px-3 py-3 font-semibold uppercase text-[10px] tracking-widest">Category total</td>
                    {totals.byCat[activeCat].byMonth.map((v, m) => (
                      <td key={m} className="px-3 py-3 text-right font-semibold">${fmt(v)}</td>
                    ))}
                    <td className="px-3 py-3 text-right font-semibold text-shell-side">${fmt(totals.byCat[activeCat].total)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
